import { createFileRoute, Link, redirect, useNavigate, useSearch } from '@tanstack/react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Loader2, Mail } from 'lucide-react'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/ui/form-field'
import { LocationCombobox } from '@/components/ui/location-combobox'
import { SuggestCombobox } from '@/components/ui/suggest-combobox'
import { meQuery, useRegisterMutation, useSendRegisterCodeMutation } from '@/api/auth'
import { ApiError } from '@/api/errors'
import { countryCodeOf, suggestCountries } from '@/lib/countries'
import { composeLocation } from '@/lib/location'

const signupSearchSchema = z.object({
  from: z.string().optional(),
})

const signupFormSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    email: z.string().email('Enter a valid email'),
    code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'),
    country: z.string().trim().min(1, 'Country is required'),
    city: z.string().trim().optional(),
    password: z.string().min(8, 'Must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  })

type SignupForm = z.infer<typeof signupFormSchema>

export const Route = createFileRoute('/signup')({
  validateSearch: signupSearchSchema,
  beforeLoad: async ({ context, search }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery())
      throw redirect({ to: search.from ?? '/dashboard' })
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) return
      throw err
    }
  },
  component: SignupPage,
})

function SignupPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/signup' })
  const register = useRegisterMutation()
  const sendCode = useSendRegisterCodeMutation()
  const [secondsLeft, setSecondsLeft] = useState(0)
  const form = useForm<SignupForm>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      code: '',
      country: 'Singapore',
      city: '',
      password: '',
      confirmPassword: '',
    },
  })

  // 国家要先确定下来，城市补全才知道该在哪个国家里找。
  const cityCountryCode = countryCodeOf(form.watch('country'))
  useEffect(() => {
    // 换国家后旧城市不再可信，直接清掉。
    form.setValue('city', '')
  }, [cityCountryCode, form])

  useEffect(() => {
    if (secondsLeft <= 0) return
    const timer = window.setInterval(() => setSecondsLeft((value) => Math.max(value - 1, 0)), 1000)
    return () => window.clearInterval(timer)
  }, [secondsLeft])

  const requestCode = () => {
    const email = form.getValues('email').trim()
    const parsed = z.string().email('Enter a valid email').safeParse(email)
    if (!parsed.success) {
      form.setError('email', { message: parsed.error.issues[0]?.message ?? 'Enter a valid email' })
      return
    }
    sendCode.mutate(
      { email },
      {
        onSuccess: () => {
          setSecondsLeft(60)
          toast.success('Verification code sent')
        },
        onError: (err) => toast.error(err.message || 'Could not send verification code'),
      },
    )
  }

  const onSubmit = ({ confirmPassword: _, country, city, ...values }: SignupForm) => {
    register.mutate({ ...values, location: composeLocation(country, city ?? '') }, {
      onSuccess: () => {
        void navigate({ to: search.from ?? '/dashboard' })
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          if (err.code === 'conflict') {
            form.setError('email', { message: 'An account with this email already exists' })
            return
          }
          if (err.fields) {
            for (const [field, message] of Object.entries(err.fields)) {
              if (
                field === 'firstName' ||
                field === 'lastName' ||
                field === 'email' ||
                field === 'code' ||
                field === 'country' ||
                field === 'location' ||
                field === 'password'
              ) {
                form.setError(field === 'location' ? 'country' : field, { message })
              }
            }
            return
          }
        }
        toast.error(err.message || 'Could not create account')
      },
    })
  }

  return (
    <div className="grid min-h-screen place-items-center bg-surface-muted px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/fuku-logo.svg" alt="Fuku AI" width={80} height={26} className="h-12 w-auto" />
          <h1 className="text-2xl font-bold tracking-tight text-ink">Create your Fuku AI account</h1>
          <p className="text-center text-sm text-muted">Start automating your job search.</p>
        </div>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4 rounded-[var(--radius-card)] border border-divider bg-surface-base p-6 shadow-card"
        >
          <div className="grid grid-cols-2 gap-3">
            <FormField
              label="First name"
              htmlFor="firstName"
              error={form.formState.errors.firstName?.message}
            >
              <Input
                id="firstName"
                autoComplete="given-name"
                aria-invalid={!!form.formState.errors.firstName}
                {...form.register('firstName')}
              />
            </FormField>
            <FormField
              label="Last name"
              htmlFor="lastName"
              error={form.formState.errors.lastName?.message}
            >
              <Input
                id="lastName"
                autoComplete="family-name"
                aria-invalid={!!form.formState.errors.lastName}
                {...form.register('lastName')}
              />
            </FormField>
          </div>

          <FormField label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              aria-invalid={!!form.formState.errors.email}
              {...form.register('email')}
            />
          </FormField>

          <FormField
            label="Email verification code"
            htmlFor="code"
            hint="The code expires in 10 minutes."
            error={form.formState.errors.code?.message}
          >
            <div className="flex gap-2">
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                aria-invalid={!!form.formState.errors.code}
                className="min-w-0 flex-1"
                {...form.register('code')}
              />
              <Button
                type="button"
                variant="secondary"
                className="min-w-28 shrink-0"
                disabled={sendCode.isPending || secondsLeft > 0}
                onClick={requestCode}
              >
                {sendCode.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : secondsLeft > 0 ? (
                  `Resend in ${secondsLeft}s`
                ) : (
                  <>
                    <Mail aria-hidden="true" />
                    Send code
                  </>
                )}
              </Button>
            </div>
          </FormField>

          <FormField
            label="Country"
            htmlFor="country"
            hint="Start typing a country name, or pick one from the list."
            error={form.formState.errors.country?.message}
          >
            <Controller
              name="country"
              control={form.control}
              render={({ field }) => (
                <SuggestCombobox
                  id="country"
                  value={field.value}
                  onChange={field.onChange}
                  suggestions={{
                    items: suggestCountries(field.value),
                    isLoading: false,
                    isUnavailable: false,
                  }}
                  itemKey={(country) => country.code}
                  selectValue={(country) => country.name}
                  renderItem={(country) => country.name}
                  placeholder="e.g. Singapore"
                  ariaInvalid={!!form.formState.errors.country}
                />
              )}
            />
          </FormField>

          {cityCountryCode && (
            <FormField
              label="City (optional)"
              htmlFor="city"
              hint="Skip it for city-states like Singapore."
              error={form.formState.errors.city?.message}
            >
              <Controller
                name="city"
                control={form.control}
                render={({ field }) => (
                  <LocationCombobox
                    id="city"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    country={cityCountryCode}
                    selectValue={(item) => item.name}
                    placeholder="e.g. Berlin"
                    ariaInvalid={!!form.formState.errors.city}
                  />
                )}
              />
            </FormField>
          )}

          <FormField label="Password" htmlFor="password" error={form.formState.errors.password?.message}>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              aria-invalid={!!form.formState.errors.password}
              {...form.register('password')}
            />
          </FormField>

          <FormField
            label="Confirm password"
            htmlFor="confirmPassword"
            error={form.formState.errors.confirmPassword?.message}
          >
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              aria-invalid={!!form.formState.errors.confirmPassword}
              {...form.register('confirmPassword')}
            />
          </FormField>

          <Button type="submit" disabled={register.isPending} aria-busy={register.isPending} className="mt-2">
            {register.isPending ? (
              <>
                <Loader2 className="animate-spin" aria-hidden="true" />
                Creating account…
              </>
            ) : (
              'Create account'
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
